import React, { createContext, useContext, useReducer, useEffect, useState } from "react";
import { IRegistrationSheetInfo, IUrlInfo, PendingRequestResponse } from "../models/commonModels";
import {
  ApiResponse,
  getPendingRequests,
  getRegistrationSheet,
  sendRegistrationSheetToAPI
} from "../repos/registrationRepos";
import { useConfigurationStore } from "./ConfigStore";
import axios, { AxiosError } from "axios";
import { IActiveState, IErrorResponse, ITenantContext, ITenantState, TenantActions, initialState } from "../models/tenantModels";

const isUrlInfo = (response: any): response is IUrlInfo => {
  if (response && typeof response === 'object') {
    return response.hasOwnProperty('url') && response.hasOwnProperty('view');
  } else {
    return false;
  }
};

const isRegistrationSheetInfo = (response: any): response is IRegistrationSheetInfo => {
  if (response && typeof response === 'object') {
    return response.hasOwnProperty('database') && response.hasOwnProperty('registrationSheetId');
  } else {
    return false;
  }
};

const TenantsReducer = (state: ITenantState, action: TenantActions): ITenantState => {
  switch (action.type) {
    case "SET_EMBEDDED_INFO":
      return {
        ...state,
        urlInfo: {
          view: 'embedded',
          url: action.url
        }
      };

    case "SET_QRCODE_INFO":
      return {
        ...state,
        urlInfo: {
          view: 'qrCode',
          url: action.url,
          title: action.title,
          label: action.label,
          text: action.text
        }
      };
    case "SET_RES_INFO":
      return {
        ...state,
        database: action.database,
        registrationSheetId: action.registrationSheetId
      };
    case "UPDATE_GUESTCARD_CONSENT":
      return {
        ...state,
        registrationSheetData: {
          ...state.registrationSheetData,
          registrationSheet: {
            ...state.registrationSheetData.registrationSheet,
            guestCardAgreement: {
              ...state.registrationSheetData.registrationSheet.guestCardAgreement,
              consented: action.payload
            }
          }
        }
      };
    case "UPDATE_ADVERT_CONSENT":
      return {
        ...state,
        registrationSheetData: {
          ...state.registrationSheetData,
          registrationSheet: {
            ...state.registrationSheetData.registrationSheet,
            advertisingAgreement: {
              ...state.registrationSheetData.registrationSheet.advertisingAgreement,
              consented: action.payload
            }
          }
        }
      };
    case "LOAD_RS_DATA":
      return {
        ...state,
        registrationSheetData: action.registrationSheet
      };
    case "CLEAR":
      return { ...initialState };
    default:
      console.warn("Unknown action type");
      return state;
  }
};

const TenantContext = createContext<ITenantContext>({
  tenant: initialState,
  dispatchTenant: () => { },
  isRequestPending: false,
  setIsRequestPending: () => { },
  isConnectionError: true,
  connectionErrorMessage: "",
  activeState: {
    registrationSheetHasArrived: false,
    embeddedHasArrived: false,
    qrCodeHasArrived: false,
    emptyResponse: false
  },
  updateActiveState: () => { },
  sendRegistrationSheet: (value: string) => {
    return new Promise((resolve, reject) => {
      resolve({ success: false });
    });
  }
});
export const TenantsProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { configuration } = useConfigurationStore()
  const [tenant, dispatchTenant] = useReducer(TenantsReducer, initialState);

  const [isRequestPending, setIsRequestPending] = useState(false);
  const [isConnectionError, setisConnectionError] = useState(true);
  const [connectionErrorMessage, setConnectionErrorMessage] = useState("");

  const [activeState, setActiveState] = useState<IActiveState>({
    registrationSheetHasArrived: false,
    embeddedHasArrived: false,
    qrCodeHasArrived: false,
    emptyResponse: true
  });
  const [lastResponse, setLastResponse] = useState<PendingRequestResponse>(null);
  const updateActiveState = (key: keyof IActiveState) => {
    setActiveState({
      registrationSheetHasArrived: false,
      embeddedHasArrived: false,
      qrCodeHasArrived: false,
      emptyResponse: false,
      [key]: true
    });
  };
  useEffect(() => {
    async function fetchData() {
      try {
        const request = await getPendingRequests(configuration.setup.deviceName, configuration.setup.tenantId);
        if (isConnectionError) {
          setisConnectionError(false)
        }
        //Check weather response is the same as before, and if so exit early
        if (JSON.stringify(lastResponse) === JSON.stringify(request)) {
          return;
        }
        /*
        1. Handle RegistrationsheetInfo Response
        */
        if (isRegistrationSheetInfo(request)) {
          dispatchTenant({ type: "SET_RES_INFO", database: request.database, registrationSheetId: request.registrationSheetId });
          const resData = await getRegistrationSheet(request.database, request.registrationSheetId, configuration.setup.tenantId);
          updateActiveState("registrationSheetHasArrived")
          dispatchTenant({ type: "LOAD_RS_DATA", registrationSheet: resData });

        } else if (isUrlInfo(request)) {
          if (request.view === 'qrCode') {
            /*
          2. Handle QR Code Response
          */
            dispatchTenant({ type: "SET_QRCODE_INFO", url: request.url, title: request.title, label: request.label, text: request.text });
            updateActiveState("qrCodeHasArrived")
          } else if (request.view === 'embedded') {
            /*
         2. Handle Embedded Response
         */
            dispatchTenant({ type: "SET_EMBEDDED_INFO", url: request.url });
            updateActiveState("embeddedHasArrived")
          }
        }
        /*
        4. Handle Empty Response
        */
        else {
          // If LastRequest was ResInfo then Reset State
          if (isRegistrationSheetInfo(lastResponse)) {
            updateActiveState("emptyResponse")
            dispatchTenant({ type: "CLEAR" });
            // If LastRequest was UrlInfo then stop polling but bevore the lastResponse has to be set to empty
          } else if (isUrlInfo(lastResponse)) {
            setIsRequestPending(false)
          }

        }
        setLastResponse(request);

      } catch (error: any) {
        setisConnectionError(true)
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError<IErrorResponse>;
          if (axiosError.response) {
            // Now TypeScript knows the structure of axiosError.response.data
            setConnectionErrorMessage(axiosError.response.data.message);
          } else if (axiosError.request) {
            // Request made but no response received
            setConnectionErrorMessage("No response from server");
          } else {
            // Error setting up the request
            setConnectionErrorMessage(axiosError.message);
          }
        } else {
          // It's not an AxiosError
          setConnectionErrorMessage("An unexpected error occurred");
        }
      }
    }
    if (isRequestPending) {
      const intervalId = setInterval(fetchData, 1500);
      return () => clearInterval(intervalId);
    }

  }, [
    isRequestPending,
    isConnectionError,
    lastResponse,
    configuration.setup.deviceName,
    configuration.setup.tenantId,
    dispatchTenant,
    getPendingRequests,
    getRegistrationSheet,
    setActiveState
  ]);


  const sendRegistrationSheet = async (signature: string): Promise<ApiResponse> => {
    try {
      await sendRegistrationSheetToAPI(signature, tenant.registrationSheetData, tenant.database, configuration.setup.tenantId);
      return {
        success: true,
      };
    } catch (error) {
      console.error("sendRegistrationSheet: Error sending data:", error);

      let errorMessage: string;
      if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = "Unknown error occurred.";
      }
      return {
        success: false,
        errorCode: 'API_ERROR',
        errorMessage: errorMessage,
      };
    }
  };
  const contextValue = {
    tenant,
    dispatchTenant,
    isRequestPending,
    isConnectionError,
    connectionErrorMessage,
    activeState,
    updateActiveState,
    setIsRequestPending,
    sendRegistrationSheet
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenantStore = (): ITenantContext => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenantsStore must be used within a TenantsProvider");
  }
  return context;
};
